import type { FC } from "react";
import { useState } from "react";
import type { SessionEducationalResourceItem } from "../../interfaces/sessionDetails";
import { useLocalization } from "../../stores/localizationContext";
import { isProbablyPlayableVideoUrl } from "../../utils/sessionEducationalResources";
import "./SessionLiveRecording.scss";

type SessionLiveRecordingProps = {
  recordings: SessionEducationalResourceItem[];
  sessionTitle: string;
  subjectLabel?: string;
};

const CARD_LABEL_KEYS = [
  "session_live_recording",
  "session_ai_session",
] as const;

export const SessionLiveRecording: FC<SessionLiveRecordingProps> = ({
  recordings,
  sessionTitle,
  subjectLabel,
}) => {
  const { t } = useLocalization();
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);

  const openResource = (item: SessionEducationalResourceItem) => {
    const url = item.educational_resource_url;
    if (isProbablyPlayableVideoUrl(url)) {
      setActiveVideoUrl(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      className="session-live-recording"
      aria-labelledby="session-recording-heading"
    >
      {!recordings.length ? (
        <div className="session-live-recording__empty">
          <p className="session-live-recording__empty-text">
            {t("session_no_recordings", "No recordings available for this session.")}
          </p>
        </div>
      ) : (
        <div className="session-live-recording__cards">
          {recordings.slice(0, 2).map((item, index) => {
          const labelKey = CARD_LABEL_KEYS[index] ?? "session_recording";
          const label =
            index < CARD_LABEL_KEYS.length
              ? t(
                  labelKey,
                  labelKey === "session_live_recording"
                    ? "Live Recording"
                    : "AI Session"
                )
              : t("session_recording", "Recording");

          const cardClass =
            index === 0
              ? "session-live-recording__card session-live-recording__card--live"
              : "session-live-recording__card session-live-recording__card--ai";

          const pillClass =
            index === 0
              ? "session-live-recording__pill session-live-recording__pill--live"
              : "session-live-recording__pill session-live-recording__pill--ai";

          return (
            <div
              key={item.educational_resource_id}
              className="session-live-recording__column"
            >
              <h2 className="session-live-recording__card-label">{label}</h2>
              <button
                type="button"
                className={cardClass}
                onClick={() => openResource(item)}
              >
                <span className="session-live-recording__card-eyebrow">
                  {sessionTitle}
                </span>
                <span className="session-live-recording__card-title">
                  {item.educational_resource_name.replace(/\.[^.]+$/, "")}
                </span>
                <span className="session-live-recording__play" aria-hidden="true">
                  <i className="f-icon play-icon" />
                </span>
                {subjectLabel && (
                  <span className={pillClass}>
                    {subjectLabel}
                  </span>
                )}
              </button>
            </div>
          );
        })}
        </div>
      )}

      {activeVideoUrl && (
        <div
          className="session-live-recording__modal"
          role="dialog"
          aria-modal="true"
          aria-label={t("session_video_dialog", "Session recording")}
        >
          <button
            type="button"
            className="session-live-recording__modal-backdrop"
            aria-label={t("close", "Close")}
            onClick={() => setActiveVideoUrl(null)}
          />
          <div className="session-live-recording__modal-content">
            <button
              type="button"
              className="session-live-recording__modal-close"
              onClick={() => setActiveVideoUrl(null)}
            >
              ×
            </button>
            <video
              className="session-live-recording__video"
              src={activeVideoUrl}
              controls
              autoPlay
            />
          </div>
        </div>
      )}
    </section>
  );
};
